import React, { useState, useEffect } from "react";
import {
  Badge, IconButton, Popover, Box, Typography,
  List, ListItem, ListItemText, Divider, CircularProgress, Tooltip
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import SnoozeIcon from "@mui/icons-material/Snooze";
import DeleteIcon from "@mui/icons-material/Delete";
import { db } from "../firebase";
import {
  collection, onSnapshot, orderBy, query,
  updateDoc, doc, writeBatch
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";

const GRAVITA_COLORI = {
  warning: "#FFA500",
  error: "#D32F2F",
  info: "#1976D2",
};

export default function Notifiche({ clientiMap, aggiornaNotificheCallback }) {
  const [notifiche, setNotifiche] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anchor, setAnchor] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const q = query(collection(db, "notifiche"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setNotifiche(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (aggiornaNotificheCallback && Object.keys(clientiMap).length > 0) {
      aggiornaNotificheCallback(clientiMap);
    }
  }, [clientiMap]);

  const oggi = new Date().toISOString().split("T")[0];
  const notificheVisibili = notifiche.filter((n) => {
    if (n.ignorata) return false;
    if (n.posticipataAl && n.posticipataAl > oggi) return false;
    return true;
  });

  const nonLette = notificheVisibili.filter((n) => !n.letta);

  async function handleApriPannello(e) {
    setAnchor(e.currentTarget);
    if (nonLette.length > 0) {
      const batch = writeBatch(db);
      nonLette.forEach((n) => {
        batch.update(doc(db, "notifiche", n.id), { letta: true });
      });
      await batch.commit();
    }
  }

  async function handlePosticipa(e, notificaId) {
    e.stopPropagation();
    const domani = new Date();
    domani.setDate(domani.getDate() + 1);
    const domaniStr = domani.toISOString().split("T")[0];
    await updateDoc(doc(db, "notifiche", notificaId), {
      posticipataAl: domaniStr,
      letta: true,
    });
  }

async function handleElimina(e, notificaId) {
  e.stopPropagation();
  if (!window.confirm("Sei sicuro di voler eliminare questa notifica?")) return;
  await updateDoc(doc(db, "notifiche", notificaId), {
    ignorata: true,
    letta: true,
  });
}

  function handleApriEvento(eventoId) {
    setAnchor(null);
    navigate(`/evento/${eventoId}`);
  }

  return (
    <>
      <IconButton onClick={handleApriPannello} sx={{ color: "white" }}>
        <Badge badgeContent={nonLette.length} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ width: 420, maxHeight: 480, overflow: "auto" }}>
          <Box sx={{ p: 2, backgroundColor: "#1A56A0" }}>
            <Typography variant="h6" color="white" fontWeight="bold">
              Notifiche
            </Typography>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress size={24} />
            </Box>
          ) : notificheVisibili.length === 0 ? (
            <Box p={3}>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Nessuna notifica
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {notificheVisibili.map((n, idx) => (
                <React.Fragment key={n.id}>
                  <ListItem
                    sx={{
                      py: 1.5, px: 2, pr: 12,
                      backgroundColor: n.letta ? "transparent" : "rgba(26,86,160,0.05)",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                      cursor: "pointer",
                    }}
                    onClick={() => handleApriEvento(n.eventoId)}
                    secondaryAction={
                      <Box display="flex" gap={0.5}>
                        <Tooltip title="Posticipa di 1 giorno">
                          <IconButton
                            size="small"
                            onClick={(e) => handlePosticipa(e, n.id)}
                            sx={{ color: "text.secondary" }}
                          >
                            <SnoozeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Elimina notifica">
                          <IconButton
                            size="small"
                            onClick={(e) => handleElimina(e, n.id)}
                            sx={{ color: "error.main" }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                  >
                    <Box sx={{
                      width: 4, height: 40, borderRadius: 2,
                      backgroundColor: GRAVITA_COLORI[n.gravita] || "#666",
                      mr: 1.5, flexShrink: 0
                    }} />
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={n.letta ? "normal" : "bold"}>
                          {n.titolo}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {n.messaggio}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {idx < notificheVisibili.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
}